import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import DateBox from "devextreme-react/date-box";
import Button from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import {useAuth} from "../../contexts/auth";
import {getKdGtp, getChartGtpPlanAnalitic, getGtpAnaliticExcel} from "../../api/plan";
import {SelectBox} from "devextreme-react/select-box";
import Toolbar, { Item } from 'devextreme-react/toolbar';
import DropDownButton from "devextreme-react/drop-down-button";
import {Buffer} from "buffer";
import AccessDenied from "../../components/access-denied/access-denied";
import {verifyAccess} from "../../utils/admin.utils";
import {getYm, getyyyyMMdd} from "../../utils/date.utils";
import {
    Chart,
    Series,
    ArgumentAxis,
    ConstantLine,
    Label,
    Legend,
    Margin,
    Tooltip,
    Export,
    Grid, ZoomAndPan, ScrollBar,
    CommonSeriesSettings
} from 'devextreme-react/chart';
import DataGrid, {Column, Paging} from "devextreme-react/data-grid";
import {CheckBox, TextBox} from "devextreme-react";
import LoadPanel from "devextreme-react/load-panel";
import './analiz-planning.scss'
import ZagolovokFromMenu from "../../components/zagolovok/zagolovok";

const PERIOD_KINDS = [
    { id: 'day', text: 'сутки' },
    { id: 'week', text: 'неделя' },
    { id: 'month', text: 'месяц' },
    { id: 'quarter', text: 'квартал' },
    { id: 'year', text: 'год' },
]

const CHART_EXPORT_ITEMS = [
    { id: 'PNG', text: 'PNG' },
    { id: 'JPEG', text: 'JPEG' },
    { id: 'PDF', text: 'PDF' },
    { id: 'SVG', text: 'SVG' },
    { id: 'print', text: 'Печать' },
]

function pad2(n) {
    return String(n).padStart(2, '0')
}

function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addDays(date, n) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n)
}

function shiftPeriod(kind, start, end, dir) {
    const s = startOfDay(start)
    const e = startOfDay(end)
    if (kind === 'day') {
        const n = addDays(s, dir)
        return [n, new Date(n)]
    }
    if (kind === 'week') {
        return [addDays(s, dir * 7), addDays(e, dir * 7)]
    }
    if (kind === 'month') {
        return rangeForKind('month', new Date(s.getFullYear(), s.getMonth() + dir, 1))
    }
    if (kind === 'quarter') {
        return rangeForKind('quarter', new Date(s.getFullYear(), s.getMonth() + dir * 3, 1))
    }
    return rangeForKind('year', new Date(s.getFullYear() + dir, 0, 1))
}

function rangeForKind(kind, from = new Date()) {
    const y = from.getFullYear()
    const m = from.getMonth()
    const d = from.getDate()
    if (kind === 'day') return [new Date(y, m, d), new Date(y, m, d)]
    if (kind === 'week') {
        const dow = from.getDay() || 7
        const mon = new Date(y, m, d - (dow - 1))
        return [mon, new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6)]
    }
    if (kind === 'month') return [new Date(y, m, 1), new Date(y, m + 1, 0)]
    if (kind === 'quarter') {
        const q = Math.floor(m / 3) * 3
        return [new Date(y, q, 1), new Date(y, q + 3, 0)]
    }
    return [new Date(y, 0, 1), new Date(y, 11, 31)]
}

function mapAnaliticRows(rows, rpp) {
    return (rows || []).map((e) => {
        const dt = String(e.dt).slice(0, 10)
        const hour = Number(e.hour)
        const hh = `${pad2(hour)}:00`
        const plan = e.plan_value != null ? Number(e.plan_value) : null
        const fact = e.fact_value != null ? Number(e.fact_value) : null
        const rppVal = rpp?.[dt]?.[hour] ?? rpp?.[dt]?.[String(hour)] ?? null
        const dif = (fact != null && plan != null) ? fact - plan : null
        const pct = (fact != null && fact !== 0 && plan != null)
            ? Math.abs(fact - plan) / Math.abs(fact) * 100
            : null
        return {
            ...e,
            dt,
            hour,
            yyyymmhh: `${dt} ${hh}`,
            plan_value: plan,
            fact_value: fact,
            rpp_value: rppVal,
            dif,
            pct,
        }
    })
}

const AnalizPlanning = () => {
    const nowRange = rangeForKind('month')
    const [periodKind, setPeriodKind] = useState('month')
    const [period1, setPeriod1] = useState(nowRange[0])
    const [period2, setPeriod2] = useState(nowRange[1])
    const { selectedDog } = useAuth()
    const [data, setData] = useState([])
    const [kdGtps, setKdGtps] = useState([])
    const [kdGtp, setKdGtp] = useState(null)
    const [xlsGtpId, setXlsGtpId] = useState(null)
    const [visiblePage, setVisiblePage] = useState(true)
    const [showDayLines, setShowDayLines] = useState(true)
    const [showRpp, setShowRpp] = useState(false)
    const [rppEnabled, setRppEnabled] = useState(false)
    const [loading, setLoading] = useState(true)
    const chartRef = useRef(null)
    // Игнор устаревших ответов при быстрой смене периода/ГТП
    const chartReqIdRef = useRef(0)
    const maxDate = useMemo(() => {
        const d = new Date()
        return new Date(d.getFullYear(), d.getMonth() + 1, d.getDate())
    }, [])

    const getGtpChart = useCallback(async () => {
        if (!kdGtp || !period1 || !period2) return
        const reqId = ++chartReqIdRef.current
        const begin = getyyyyMMdd(period1)
        const end = getyyyyMMdd(period2)
        setLoading(true)
        try {
            const res = await getChartGtpPlanAnalitic(
                kdGtp,
                getYm(period1),
                selectedDog.kodDog,
                begin,
                end
            )
            if (reqId !== chartReqIdRef.current) return
            if (res.isOk) {
                setRppEnabled(!!res.show_rpp)
                setData(mapAnaliticRows(res.result, res.rpp || {}))
            } else {
                setData([])
                notify(res.message || 'Не удалось загрузить аналитику', 'error', 4000)
            }
        } finally {
            if (reqId === chartReqIdRef.current) setLoading(false)
        }
    }, [kdGtp, period1, period2, selectedDog.kodDog])

    useEffect(() => {
        const pageRoles = [2]
        if (!verifyAccess(pageRoles, selectedDog)) {
            setVisiblePage(false)
            return
        }
        (async function(){
            setVisiblePage(true)
            setLoading(true)
            const res = await getKdGtp(selectedDog.kodDog)
            if (res.isOk && res.result && res.result.length > 0) {
                setKdGtps(res.result)
                setKdGtp(res.result[0].kod_gtp)
                setXlsGtpId(res.result[0].xls_gtp_id)
                setRppEnabled(!!res.result[0].show_rpp)
            } else {
                setData([])
                setKdGtps([])
                setKdGtp(null)
                setXlsGtpId(null)
                setRppEnabled(false)
                setLoading(false)
            }
        })()
    }, [selectedDog])

    useEffect(() => {
        (async function(){
            await getGtpChart()
        })()
    }, [getGtpChart])

    const onValueChanged = (e) => {
        setKdGtp(e.value)
        const gtp = kdGtps.find(l => l.kod_gtp === e.value)
        if (gtp) {
            setXlsGtpId(gtp.xls_gtp_id)
            setRppEnabled(!!gtp.show_rpp)
        }
    }

    const onPeriodKindChanged = (e) => {
        const kind = e.value
        setPeriodKind(kind)
        const [a, b] = rangeForKind(kind, new Date())
        setPeriod1(a)
        setPeriod2(b)
    }

    const onDateBox1Changed = (e) => {
        if (e.event === undefined) return
        const next = startOfDay(e.value)
        setPeriod1(next)
        if (next > period2) setPeriod2(next)
    }

    const onDateBox2Changed = (e) => {
        if (e.event === undefined) return
        const next = startOfDay(e.value)
        setPeriod2(next)
        if (next < period1) setPeriod1(next)
    }

    const shiftPeriodBy = (dir) => {
        if (!period1 || !period2) return
        const [a, b] = shiftPeriod(periodKind, period1, period2, dir)
        if (dir > 0 && a > maxDate) return
        setPeriod1(a)
        setPeriod2(b > maxDate ? maxDate : b)
    }

    const loadFileExcel = async () => {
        try {
            const res = await getGtpAnaliticExcel(kdGtp, getYm(period1), selectedDog.kodDog)
            if (res.isOk) {
                const file = res.result.content
                const filename = res.result.filename
                const fileDownload = require('js-file-download')
                fileDownload(Buffer.from(file), filename)
            } else {
                notify(res.message, 'error', 5000)
            }
        } catch (e) {
            notify(e.message, 'error', 5000)
        }
    }

    const resetZoom = useCallback(() => {
        chartRef.current?.instance?.resetVisualRange()
    }, [])

    const onChartExport = useCallback((e) => {
        const inst = chartRef.current?.instance
        if (!inst) return
        if (e.itemData.id === 'print') inst.print()
        else inst.exportTo('План и факт', e.itemData.id)
    }, [])

    const customizeTooltip = (pointInfo) => ({
        html:
            `<div><div class="tooltip-header">Дата: ${
                pointInfo.argument
            }</div><div class="tooltip-body">${
                (pointInfo.points || []).map(p =>
                    `<div class="series-name"><span>${p.seriesName}</span>: </div>` +
                    `<div class="value-text"><span>${p.valueText}</span></div>`
                ).join('')
            }</div></div>`,
    })

    const dayStarts = useMemo(() => {
        const seen = new Set()
        const lines = []
        for (const row of data) {
            if (!seen.has(row.dt)) {
                seen.add(row.dt)
                lines.push(row.yyyymmhh)
            }
        }
        return lines
    }, [data])

    const hasRppPoints = useMemo(
        () => showRpp && rppEnabled && data.some(r => r.rpp_value != null),
        [showRpp, rppEnabled, data]
    )

    const totals = useMemo(() => {
        let plan = 0, fact = 0, n = 0, sumPct = 0, maxPct = 0
        for (const row of data) {
            if (row.plan_value != null) plan += row.plan_value
            if (row.fact_value != null) fact += row.fact_value
            if (row.pct != null) {
                sumPct += row.pct
                n++
                if (row.pct > maxPct) maxPct = row.pct
            }
        }
        return {
            plan,
            fact,
            avgPct: n ? sumPct / n : 0,
            maxPct,
        }
    }, [data])

    const chartTitle = useMemo(() => {
        const a = getyyyyMMdd(period1)
        const b = getyyyyMMdd(period2)
        return a === b ? a : `${a} — ${b}`
    }, [period1, period2])

    const formatNum = { type: 'fixedPoint', precision: 3 }

    return (
        <>{visiblePage ?
            <div className={'content-block dx-card responsive-paddings analiz-planning-page'}>
                <LoadPanel visible={loading} />
                <i className="dx-icon dx-icon-event icon-header-h3 main-color main-color-light"></i>
                <h3><strong><ZagolovokFromMenu/></strong></h3>
                {kdGtp ?
                    <>
                        <SelectBox labelMode={'floating'} label={'ГТП'} className="big-input"
                                   noDataText="Нет данных"
                                   placeholder="Выберите ГТП..."
                                   dataSource={kdGtps}
                                   displayExpr="name"
                                   valueExpr="kod_gtp"
                                   value={kdGtp}
                                   columnAutoWidth={true}
                                   onValueChanged={onValueChanged}
                                   searchEnabled={true}
                                   searchExpr={['name']}
                                   searchMode={"contains"}
                        />
                        <div className="row mb-3 mt-3 analiz-period-bar" >
                            <div className="col-12 big-input">
                                <Toolbar>
                                    <Item location="before" locateInMenu="never">
                                        <SelectBox
                                            dataSource={PERIOD_KINDS}
                                            valueExpr="id"
                                            displayExpr="text"
                                            value={periodKind}
                                            label="Период отображения"
                                            labelMode="floating"
                                            width={180}
                                            onValueChanged={onPeriodKindChanged}
                                        />
                                    </Item>
                                    <Item location="before" locateInMenu="auto">
                                        <Button
                                            style={{marginTop: '7px'}}
                                            icon="chevronprev"
                                            hint="Предыдущий период"
                                            stylingMode="outlined"
                                            onClick={() => shiftPeriodBy(-1)}
                                        />
                                    </Item>
                                    <Item location="before" locateInMenu="auto">
                                        <DateBox
                                            openOnFieldClick={true}
                                            displayFormat='dd.MM.yyyy'
                                            useMaskBehavior={true}
                                            value={period1}
                                            label={'Начало:'}
                                            max={maxDate}
                                            onValueChanged={onDateBox1Changed}
                                        />
                                    </Item>
                                    <Item location="before" locateInMenu="auto">
                                        <DateBox
                                            openOnFieldClick={true}
                                            displayFormat='dd.MM.yyyy'
                                            useMaskBehavior={true}
                                            value={period2}
                                            label={'Конец:'}
                                            min={period1}
                                            max={maxDate}
                                            onValueChanged={onDateBox2Changed}
                                        />
                                    </Item>
                                    <Item location="before" locateInMenu="auto">
                                        <Button
                                            style={{marginTop: '7px'}}
                                            icon="chevronnext"
                                            hint="Следующий период"
                                            stylingMode="outlined"
                                            onClick={() => shiftPeriodBy(1)}
                                        />
                                    </Item>
                                    <Item location="before" locateInMenu="auto">
                                        <TextBox value={xlsGtpId} readOnly={true} label={'Код:'} width={'auto'}/>
                                    </Item>
                                    <Item location="before" locateInMenu="auto">
                                        <CheckBox
                                            value={showDayLines}
                                            onValueChanged={(e) => setShowDayLines(!!e.value)}
                                            text="Линии суток"
                                        />
                                    </Item>
                                    {rppEnabled &&
                                        <Item location="before" locateInMenu="auto">
                                            <CheckBox
                                                value={showRpp}
                                                onValueChanged={(e) => setShowRpp(!!e.value)}
                                                text="РПП"
                                            />
                                        </Item>
                                    }
                                    <Item location="after" locateInMenu="auto" widget="dxButton">
                                        <Button hint='Обновить' text='Обновить' icon="refresh" disabled={loading} onClick={() => {getGtpChart()}} />
                                    </Item>
                                    <Item location="after" locateInMenu="auto" widget="dxButton">
                                        <Button icon='xlsfile' hint='Выгрузить в Excel' text='Выгрузить в Excel' onClick={() => {loadFileExcel()}} />
                                    </Item>
                                </Toolbar>
                            </div>
                        </div>
                        <div className="row analiz-chart-block">
                            <div className="col-12 analiz-chart-wrap">
                                <div className="analiz-chart-head">
                                    <div className="analiz-chart-head__title">План и факт, {chartTitle}</div>
                                    <div className="analiz-chart-head__actions">
                                        <Button text="Сбросить масштаб" onClick={resetZoom} />
                                        <DropDownButton
                                            icon="menu"
                                            hint="Экспорт"
                                            showArrowIcon={false}
                                            stylingMode="text"
                                            items={CHART_EXPORT_ITEMS}
                                            displayExpr="text"
                                            keyExpr="id"
                                            onItemClick={onChartExport}
                                        />
                                    </div>
                                </div>
                                <Chart
                                    dataSource={data}
                                    ref={chartRef}
                                    id='chart-analiz'
                                    palette="Material"
                                >
                                    <ScrollBar visible={true} position={'bottom'} />
                                    <ZoomAndPan
                                        argumentAxis="both"
                                        dragToZoom={true}
                                        allowMouseWheel={true}
                                        panKey="shift"
                                    />
                                    <CommonSeriesSettings argumentField="yyyymmhh" type="line" />
                                    <Series
                                        valueField='plan_value'
                                        name='План'
                                        color="#1565C0"
                                    />
                                    <Series
                                        valueField='fact_value'
                                        name='Факт'
                                        color="#C62828"
                                    />
                                    {hasRppPoints &&
                                        <Series
                                            valueField='rpp_value'
                                            name='РПП'
                                            color="#F9A825"
                                        />
                                    }
                                    <Margin bottom={20} />
                                    <ArgumentAxis
                                        valueMarginsEnabled={false}
                                        discreteAxisDivisionMode="crossLabels"
                                    >
                                        <Grid visible={false} />
                                        {showDayLines && dayStarts.map((v) => (
                                            <ConstantLine key={v} value={v} width={1} color="#bdbdbd" dashStyle="dash">
                                                <Label visible={false} />
                                            </ConstantLine>
                                        ))}
                                    </ArgumentAxis>
                                    <Legend
                                        verticalAlignment="bottom"
                                        horizontalAlignment="center"
                                        itemTextPosition="bottom"
                                    />
                                    <Export enabled={false} fileName="План и факт" />
                                    <Tooltip enabled={true} shared={true} customizeTooltip={customizeTooltip} />
                                </Chart>
                            </div>
                        </div>

                        <div className="analiz-summary">
                            <div>Всего запланировано, МВтч: <strong>{totals.plan.toFixed(3)}</strong></div>
                            <div>Всего по факту, МВтч: <strong>{totals.fact.toFixed(3)}</strong></div>
                            <div>Среднее отклонение, %: <strong>{totals.avgPct.toFixed(2)}</strong></div>
                            <div>Максимальное отклонение, %: <strong>{totals.maxPct.toFixed(2)}</strong></div>
                        </div>

                        <DataGrid
                            className="mt-3"
                            dataSource={data}
                            keyExpr="yyyymmhh"
                            showBorders={true}
                            columnAutoWidth={true}
                        >
                            <Paging defaultPageSize={24} />
                            <Column dataField="dt" caption="Дата" />
                            <Column dataField="hour" caption="Час" width={70} />
                            <Column dataField="fact_value" caption="Факт, МВтч" dataType="number" format={formatNum} />
                            <Column dataField="plan_value" caption="План, МВтч" dataType="number" format={formatNum} />
                            <Column dataField="dif" caption="Отклонение, МВтч" dataType="number" format={formatNum} />
                            <Column dataField="pct" caption="Отклонение, %" dataType="number" format={{ type: 'fixedPoint', precision: 2 }} />
                            {hasRppPoints &&
                                <Column dataField="rpp_value" caption="РПП, МВтч" dataType="number" format={formatNum} />
                            }
                        </DataGrid>
                    </>
                 :
                <p>Для данного договора планирование потребления не предусмотрено</p>
                }

            </div>
            : <AccessDenied/>}
        </>
    )
}

export default AnalizPlanning
